import { useEffect, useMemo, useState } from 'react'
import { Link as RouterLink, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Link,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import 'dayjs/locale/ru'
import 'dayjs/locale/en'
import { Home as HomeIcon, NavigateNext as NavigateNextIcon } from '@mui/icons-material'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { useAuthStore } from '@/stores/authStore'
import type { Homework } from '@/types'
import { homeworksApi } from '@/api/homeworks'
import { ROUTES } from '@/utils/constants'
import '@/styles/dashboard.css'

function formatStudentNameFromHomework(hw: Homework): string {
  const s = hw.student
  if (!s) return ''
  return (
    s.display_name?.trim() ||
    [s.surname, s.first_name].filter((p): p is string => Boolean(p?.trim())).join(' ').trim() ||
    s.email?.trim() ||
    ''
  )
}

export function UserHomeworksPage() {
  const { t, i18n } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const userId = id ? parseInt(id, 10) : NaN

  const authUser = useAuthStore((s) => s.user)
  const authUserId = authUser?.id ?? null
  const roles = authUser?.roles ?? []
  const isAdminRole = roles.some((r) => r.slug === 'admin')

  const formatCreatedAt = (iso?: string | null) => {
    if (!iso) return '—'
    dayjs.locale(i18n.language?.startsWith('en') ? 'en' : 'ru')
    return dayjs(iso).format('DD.MM.YYYY HH:mm')
  }

  const [homeworks, setHomeworks] = useState<Homework[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const title = useMemo(() => {
    return t('homeworks.userTitle', 'Домашние задания', { userId: userId || '—' })
  }, [t, userId])

  const fetchHomeworks = async () => {
    if (!Number.isFinite(userId)) return
    if (!authUserId) return
    setLoading(true)
    setError(null)
    try {
      const res = await homeworksApi.getByUser(userId)
      setHomeworks(res)
    } catch (e: unknown) {
      const err = e as { message?: string }
      setError(err.message || 'Ошибка загрузки домашних заданий')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchHomeworks().catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, authUserId])

  const handleCheck = async (homeworkId: number) => {
    if (!authUserId) return
    try {
      await homeworksApi.checkByUser(userId, homeworkId, { action: 'approve' })
      await fetchHomeworks()
    } catch (e: unknown) {
      const err = e as { message?: string }
      setError(err.message || 'Ошибка проверки домашнего задания')
    }
  }

  return (
    <DashboardLayout>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link component={RouterLink} to={ROUTES.DASHBOARD} underline="hover" sx={{ display: 'flex', alignItems: 'center' }}>
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {t('dashboard.home')}
          </Link>
          <Typography color="text.primary">{title}</Typography>
        </Breadcrumbs>

        <Typography variant="h4" component="h1" fontWeight={700} sx={{ mb: 2 }}>
          {title}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 2 }} />
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('homeworks.tableCreatedAt', 'Дата создания')}</TableCell>
                  <TableCell>{t('homeworks.tableTask', 'Задание')}</TableCell>
                  <TableCell>{t('homeworks.tableStudent', 'Ученик')}</TableCell>
                  <TableCell>{t('homeworks.tableStatus', 'Статус')}</TableCell>
                  <TableCell align="right">{t('common.actions', 'Действия')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {homeworks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography color="text.secondary">{t('homeworks.empty', 'Нет заданий')}</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  homeworks.map((hw) => {
                    const isChecked = Boolean(hw.checked_at)
                    const isDone = Boolean(hw.completed_at)
                    const studentLabel =
                      formatStudentNameFromHomework(hw) || t('homeworks.studentFallback', { id: userId })
                    return (
                      <TableRow key={hw.id}>
                        <TableCell>
                          <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                            {formatCreatedAt(hw.created_at)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={700}>
                            {hw.lesson_id ? `Lesson #${hw.lesson_id}` : `HW #${hw.id}`}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                            {hw.task_text ?? ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Link
                            component={RouterLink}
                            to={isAdminRole ? ROUTES.ADMIN_USER_VIEW(userId) : `/users/${userId}`}
                            underline="hover"
                            sx={{ fontWeight: 600, fontSize: '0.875rem' }}
                          >
                            {studentLabel}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {isChecked
                              ? t('homeworks.statusChecked', 'Проверено')
                              : isDone
                                ? t('homeworks.statusCompleted', 'Выполнено (ожидает проверки)')
                                : t('homeworks.statusAssigned', 'Назначено')}
                          </Typography>
                          {hw.student_answer_text && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                              {t('homeworks.answer', 'Ответ')}: {hw.student_answer_text}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                              size="small"
                              variant="contained"
                              disabled={!authUserId || isChecked}
                              onClick={() => handleCheck(hw.id)}
                            >
                              {t('homeworks.checkAction', 'Проверить')}
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </DashboardLayout>
  )
}

